/**
 * BugReportDialog Component
 *
 * Modal dialog for submitting bug reports with screenshot, console log
 * collection, and browser information gathering.
 *
 * Features:
 * - Accepts pre-captured screenshot (taken before modal opens to avoid modal in screenshot)
 * - Console log collection via useConsoleCapture hook
 * - Browser information gathering (user agent, viewport, etc.)
 * - Form validation and submission to service ticket API
 * - Manual screenshot retake option
 *
 * @author American Police Training
 * @version 1.1.0
 */
import React, { useState, useEffect } from 'react';
// import { logger } from "../../utils/logger";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Typography, CircularProgress, Alert, Snackbar, Paper, } from '@mui/material';
import { ConsoleLogEntry } from '../../hooks/useConsoleCapture';
import { logger } from "../../utils/logger";
import { submitManualBugReport } from '../../utils/reportClientIncident';
import {
  IT_TEAM_NOTIFIED_DETAIL,
  IT_TEAM_NOTIFIED_TOAST_MS,
} from '../../../shared/utils/errorCapture/itTeamNotifiedCopy';
interface BugReportDialogProps {
    open: boolean;
    onClose: () => void;
    getCapturedLogs: () => ConsoleLogEntry[];
    initialScreenshot?: string | null;
}
export const BugReportDialog: React.FC<BugReportDialogProps> = ({ open, onClose, getCapturedLogs, initialScreenshot, }) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState<string | null>(null);
    const [screenshot, setScreenshot] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [capturing, setCapturing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [ticketNumber, setTicketNumber] = useState<string | null>(null);
    const [hideModal, setHideModal] = useState(false);
    // Use initial screenshot if provided, otherwise capture when dialog opens
    useEffect(() => {
        if (open) {
            if (initialScreenshot) {
                // Use pre-captured screenshot (taken before modal opened)
                setScreenshot(initialScreenshot);
            }
            else if (!screenshot) {
                // Fallback: capture screenshot if none provided
                captureScreenshot();
            }
        }
    }, [open, initialScreenshot]);
    /**
     * Capture screenshot of the current page
     * Temporarily hides the modal to prevent it from appearing in the screenshot
     */
    const captureScreenshot = async (): Promise<void> => {
        setCapturing(true);
        try {
            // Hide modal temporarily to prevent it from appearing in screenshot
            setHideModal(true);
            // Wait for modal to hide before capturing
            await new Promise(resolve => setTimeout(resolve, 100));
            const html2canvas = (await import('html2canvas')).default;
            const canvas = await html2canvas(document.body, {
                allowTaint: true,
                useCORS: true,
                logging: false,
                width: window.innerWidth,
                height: window.innerHeight,
                windowWidth: window.innerWidth,
                windowHeight: window.innerHeight,
            });
            const dataUrl = canvas.toDataURL('image/png', 0.8);
            setScreenshot(dataUrl);
            // Show modal again
            setHideModal(false);
        }
        catch (err) {
            logger.error('Failed to capture screenshot:', err, {});
            setError('Failed to capture screenshot, but you can still submit the report');
            setHideModal(false); // Ensure modal is shown even if capture fails
        }
        finally {
            setCapturing(false);
        }
    };
    /**
     * Gather browser information
     */
    const getBrowserInfo = () => {
        return {
            userAgent: navigator.userAgent,
            viewport: {
                width: window.innerWidth,
                height: window.innerHeight,
            },
            screen: {
                width: window.screen.width,
                height: window.screen.height,
                colorDepth: window.screen.colorDepth,
            },
            url: window.location.href,
            timestamp: new Date().toISOString(),
            language: navigator.language,
            languages: navigator.languages,
            platform: navigator.platform,
            cookiesEnabled: navigator.cookieEnabled,
            onLine: navigator.onLine,
            doNotTrack: navigator.doNotTrack,
        };
    };
    /**
     * Get Redux state if available
     */
    const getPageState = () => {
        try {
            // Try to access Redux store from window if available
            const win = window as any;
            if (win.__REDUX_DEVTOOLS_EXTENSION__) {
                return {
                    redux: 'Redux DevTools Extension detected',
                    note: 'State not captured for privacy',
                };
            }
            return {};
        }
        catch (err) {
            return {};
        }
    };
    /**
     * Validate email format
     */
    const validateEmail = (emailValue: string): boolean => {
        if (!emailValue.trim()) {
            return true; // Email is optional
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(emailValue);
    };
    /**
     * Handle email change with validation
     */
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const newEmail = e.target.value;
        setEmail(newEmail);
        if (newEmail.trim() && !validateEmail(newEmail)) {
            setEmailError('Please enter a valid email address');
        }
        else {
            setEmailError(null);
        }
    };
    /**
     * Handle form submission
     */
    const handleSubmit = async (): Promise<void> => {
        // Validation
        if (!title.trim()) {
            setError('Please provide a title for the bug report');
            return;
        }
        // Validate email if provided
        if (email.trim() && !validateEmail(email)) {
            setError('Please enter a valid email address');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            // Gather all bug report data
            const consoleLogs = getCapturedLogs().slice(-50); // Last 50 logs
            const browserInfo = getBrowserInfo();
            const pageState = getPageState();
            const bugReport = {
                title: title.trim(),
                description: description.trim() || undefined,
                email: email.trim() || undefined,
                screenshot,
                consoleLogs,
                browserInfo,
                pageUrl: window.location.href,
                pageState,
            };
            const result = await submitManualBugReport(bugReport);
            setTicketNumber(result.ticketNumber);
            // Clear form and close dialog immediately
            setTitle('');
            setDescription('');
            setEmail('');
            setEmailError(null);
            setScreenshot(null);
            onClose();
            // Show success message after dialog closes
            setSuccess(true);
        }
        catch (err) {
            logger.error('Failed to submit bug report:', err, {});
            setError(err instanceof Error ? err.message : 'Failed to submit bug report. Please try again.');
        }
        finally {
            setLoading(false);
        }
    };
    /**
     * Handle dialog close
     */
    const handleClose = (): void => {
        if (!loading) {
            setTitle('');
            setDescription('');
            setEmail('');
            setEmailError(null);
            setScreenshot(null);
            setError(null);
            // Don't reset success state here - let it persist for the snackbar
            onClose();
        }
    };
    /**
     * Retry screenshot capture
     */
    const handleRetryScreenshot = (): void => {
        setScreenshot(null);
        captureScreenshot();
    };
    return (<>
      <Dialog open={open && !hideModal} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{
            sx: {
                borderRadius: 2,
            },
        }}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" component="span">
              Report a Bug
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Title Field */}
            <TextField fullWidth label="Bug Title" value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="Brief description of the issue" disabled={loading} error={!title.trim() && error !== null} helperText={!title.trim() && error !== null ? 'Title is required' : ''}/>

            {/* Description Field */}
            <TextField fullWidth label="Description (Optional)" value={description} onChange={(e) => setDescription(e.target.value)} multiline rows={4} placeholder="Detailed description of what happened and what you expected..." disabled={loading}/>

            {/* Email Field */}
            <TextField fullWidth label="Email Address (Optional)" value={email} onChange={handleEmailChange} type="email" placeholder="your.email@example.com" disabled={loading} error={!!emailError} helperText={emailError || 'We\'ll notify you about updates to your bug report'}/>

            {/* Screenshot Preview */}
            {capturing && (<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                <CircularProgress size={20}/>
                <Typography variant="body2" color="text.secondary">
                  Capturing screenshot...
                </Typography>
              </Box>)}

            {screenshot && (<Box>
                <Typography variant="subtitle2" gutterBottom>
                  Screenshot Preview:
                </Typography>
                <Paper elevation={2} sx={{
                p: 1,
                bgcolor: 'grey.100',
                position: 'relative',
            }}>
                  <img src={screenshot} alt="Bug report screenshot" style={{
                maxWidth: '100%',
                maxHeight: '300px',
                display: 'block',
                margin: '0 auto',
                borderRadius: '4px',
            }}/>
                  <Button size="small" onClick={handleRetryScreenshot} sx={{ mt: 1 }} disabled={loading || capturing}>
                    Retake Screenshot
                  </Button>
                </Paper>
              </Box>)}

            {/* Info Text */}
            <Alert severity="info" sx={{ mt: 1 }}>
              This report will automatically include:
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Screenshot of the current page</li>
                <li>Recent console logs (last 50 entries)</li>
                <li>Browser information (user agent, viewport size)</li>
                <li>Current page URL</li>
              </ul>
            </Alert>

            {/* Error Alert */}
            {error && (<Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>)}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" color="error" disabled={loading || !title.trim() || capturing || !!emailError} startIcon={loading ? <CircularProgress size={16}/> : null}>
            {loading ? 'Submitting...' : 'Submit Bug Report'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar open={success} autoHideDuration={IT_TEAM_NOTIFIED_TOAST_MS} onClose={() => {
            setSuccess(false);
            setTicketNumber(null);
        }} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert
          severity="success"
          onClose={() => {
            setSuccess(false);
            setTicketNumber(null);
          }}
          sx={{ maxWidth: 520, whiteSpace: 'normal', alignItems: 'flex-start' }}
        >
          Bug report submitted successfully
          {ticketNumber ? ` (Ticket #${ticketNumber}).` : '.'}
          {' '}
          {IT_TEAM_NOTIFIED_DETAIL}
        </Alert>
      </Snackbar>
    </>);
};
export default BugReportDialog;
